import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, Trophy, TrendingUp, Calendar, DollarSign, Award } from 'lucide-react'
import { config, useClientConfig } from '@workspace/public-utils'
import { Seo } from '../components/Seo'

// LotteryBall component for displaying numbers with Korean lottery colors
const LotteryBall = ({ number, isBonus = false, size = "lg" }: {
  number: number;
  isBonus?: boolean;
  size?: "sm" | "md" | "lg"
}) => {
  const getNumberStyle = (num: number) => {
    if (isBonus) return "lottery-number-bonus"
    if (num <= 10) return "lottery-number-1-10"
    if (num <= 20) return "lottery-number-11-20"
    if (num <= 30) return "lottery-number-21-30"
    if (num <= 40) return "lottery-number-31-40"
    return "lottery-number-41-45"
  }

  const sizeClasses = {
    sm: "w-10 h-10 text-base",
    md: "w-16 h-16 text-xl",
    lg: "w-20 h-20 text-2xl md:w-24 md:h-24 md:text-3xl"
  }

  return (
    <motion.div
      className={`${getNumberStyle(number)} ${sizeClasses[size]} rounded-full flex items-center justify-center text-white font-black shadow-2xl`}
      initial={{ scale: 0, rotate: -180 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ duration: 0.6, delay: (number % 6) * 0.1 }}
    >
      {number}
    </motion.div>
  )
}

const LotteryResultDetailPage: React.FC = () => {
  const clientConfig = useClientConfig()
  const { roundNumber } = useParams<{ roundNumber: string }>()
  const navigate = useNavigate()
  const [result, setResult] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const seo = (
    <Seo
      title={roundNumber ? `로또 ${roundNumber}회 당첨번호` : '로또 당첨번호'}
      description={
        roundNumber
          ? `로또 ${roundNumber}회 당첨번호, 보너스번호, 1등 당첨금 정보를 확인하세요.`
          : '로또 회차별 당첨번호와 당첨금 정보를 확인하세요.'
      }
      canonicalPath={roundNumber ? `/results/${roundNumber}` : '/results'}
      ogType="article"
    />
  )

  useEffect(() => {
    const fetchResultDetail = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const response = await fetch(`${config.API_BASE_URL}/api/v1/public/lottery/results/${roundNumber}`)
        
        if (!response.ok) {
          throw new Error('결과를 불러올 수 없습니다')
        }
        
        const data = await response.json()
        
        // Transform the data to match expected format
        const transformedData = {
          round_number: data.round_number,
          draw_date: data.draw_date,
          numbers: data.numbers,
          bonus_number: data.bonus_number,
          first_prize_winners: data.first_prize_winners,
          first_prize_amount: data.first_prize_amount,
          total_sales: data.total_sales
        }
        
        setResult(transformedData)
      } catch (err) {
        console.error('Error fetching result:', err)
        setError('결과를 불러오는 중 오류가 발생했습니다')
      } finally {
        setLoading(false)
      }
    }

    if (roundNumber) {
      fetchResultDetail()
    }
  }, [roundNumber])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-happiness-secondary/10 via-white to-happiness-primary/10 pt-16">
        {seo}
        <div className="container mx-auto px-4 py-20">
          <div className="flex justify-center items-center h-64">
            <div className="spinner border-happiness-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-happiness-secondary/10 via-white to-happiness-primary/10 pt-16">
        {seo}
        <div className="container mx-auto px-4 py-20">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || '결과를 찾을 수 없습니다'}</p>
            <Link to="/results" className="btn-primary">
              목록으로 돌아가기
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-happiness-secondary/10 via-white to-happiness-primary/10 pt-16">
      {seo}
      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/results')}
            className="flex items-center gap-2 text-gray-600 hover:text-happiness-primary transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            목록으로 돌아가기
          </button>
        </motion.div>

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-happiness-primary to-happiness-secondary rounded-full text-white font-black text-2xl mb-4 shadow-xl">
            {result.round_number}
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-2">
            제 {result.round_number}회 추첨 결과
          </h1>
          <p className="text-xl text-gray-600">{result.draw_date}</p>
        </motion.div>

        {/* Winning Numbers - Main Focus */}
        <motion.div
          className="korean-card korean-card-premium p-8 md:p-12 mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">당첨번호</h2>
            <p className="text-gray-600">Winning Numbers</p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6">
            {result.numbers?.map((number: number, index: number) => (
              <LotteryBall key={index} number={number} size="lg" />
            ))}
            {result.bonus_number && (
              <>
                <div className="flex items-center mx-2">
                  <span className="text-3xl text-gray-400 font-bold">+</span>
                </div>
                <div>
                  <LotteryBall number={result.bonus_number} isBonus={true} size="lg" />
                  <p className="text-center text-sm text-gray-600 mt-2 font-bold">보너스</p>
                </div>
              </>
            )}
          </div>
        </motion.div>

        {/* Prize Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* 1st Prize Winners */}
          <motion.div
            className="korean-card bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-yellow-800">1등 당첨자</p>
                <p className="text-xs text-yellow-700">1st Prize Winners</p>
              </div>
            </div>
            <p className="text-3xl md:text-4xl font-black text-yellow-900">
              {result.first_prize_winners ? `${result.first_prize_winners}명` : '-'}
            </p>
          </motion.div>

          {/* 1st Prize Amount */}
          <motion.div
            className="korean-card bg-gradient-to-br from-green-50 to-green-100 border-green-200 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-green-800">1등 당첨금</p>
                <p className="text-xs text-green-700">1st Prize Amount</p>
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-black text-green-900">
              {result.first_prize_amount 
                ? `${(result.first_prize_amount / 100000000).toFixed(1)}억원`
                : '-'}
            </p>
          </motion.div>

          {/* Total Sales */}
          <motion.div
            className="korean-card bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-blue-800">총 판매액</p>
                <p className="text-xs text-blue-700">Total Sales</p>
              </div>
            </div>
            <p className="text-2xl md:text-3xl font-black text-blue-900">
              {result.total_sales 
                ? `${(result.total_sales / 100000000).toFixed(0)}억원`
                : '-'}
            </p>
          </motion.div>
        </div>

        {/* Additional Info */}
        <motion.div
          className="korean-card p-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 className="text-xl font-black text-gray-900 mb-4">추첨 정보</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-happiness-primary" />
              <div>
                <p className="text-sm text-gray-500">추첨일</p>
                <p className="font-bold">{result.draw_date}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-happiness-primary" />
              <div>
                <p className="text-sm text-gray-500">회차</p>
                <p className="font-bold">제 {result.round_number}회</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center p-8 bg-gradient-to-r from-happiness-primary to-happiness-secondary rounded-2xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <h3 className="text-2xl md:text-3xl font-black text-white mb-4">
            AI 분석으로 당신만의 행운 번호를 만드세요!
          </h3>
          <p className="text-white/90 mb-6">
            {clientConfig.smartSystemCta}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/plans" className="btn-gold text-lg px-8 py-3">
              구독 플랜 보기
            </Link>
            <Link to="/results" className="bg-white text-happiness-primary font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors text-lg">
              다른 결과 보기
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LotteryResultDetailPage

